import { motion } from "framer-motion";
import { Calendar, MousePointerClick } from "lucide-react";
import SceneShell from "../components/SceneShell";
import { GOLD_GRADIENT } from "../components/ProductLogo";

/**
 * Scene SevenDays — Section 6 elevator pitch.
 * Sits between the SOCIAL WEEK reveal (Scene33_LeadsMeTo) and the rough-maths
 * walkthrough. Two-line declarative reveal:
 *
 *   "7 days of social."
 *   "One press of a button."
 *
 * Slow, cinematic. Calendar + click glyphs anchor the meaning without
 * cluttering the line.
 */

const GOLD = "#e9a820";

export default function SceneSevenDays() {
  return (
    <SceneShell variant="ink" align="center" data-testid="scene-seven-days">
      {/* Slow gold pulse */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(ellipse at center, rgba(212,148,10,0.16) 0%, transparent 60%)",
            "radial-gradient(ellipse at center, rgba(233,168,32,0.28) 0%, transparent 60%)",
            "radial-gradient(ellipse at center, rgba(212,148,10,0.16) 0%, transparent 60%)",
          ],
        }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative max-w-[1500px] mx-auto w-full text-center flex flex-col items-center">
        {/* LINE 1 — 7 days of social. */}
        <motion.div
          initial={{ opacity: 0, y: 24, filter: "blur(20px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center justify-center gap-5 sm:gap-7"
          data-testid="seven-days-line-1"
        >
          <Calendar
            className="shrink-0 w-10 h-10 sm:w-14 sm:h-14"
            style={{
              color: GOLD,
              filter: "drop-shadow(0 0 24px rgba(233,168,32,0.55))",
            }}
            strokeWidth={1.6}
          />
          <h2
            className="kn-display text-white leading-[0.95]"
            style={{
              fontSize: "clamp(60px, 9vw, 140px)",
              fontWeight: 900,
              letterSpacing: "-0.04em",
              textShadow: "0 0 60px rgba(233,168,32,0.30)",
            }}
          >
            <span style={{ color: "#e2e8f0" }}>7 days</span>{" "}
            <span style={{ color: "rgba(255,255,255,0.55)" }}>of</span>{" "}
            <span
              style={{
                background: GOLD_GRADIENT,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              social.
            </span>
          </h2>
        </motion.div>

        {/* Hairline divider */}
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 1.2, delay: 1.6, ease: [0.16, 1, 0.3, 1] }}
          className="my-10 sm:my-14 h-px w-40 sm:w-60 origin-center"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(233,168,32,0.85) 50%, transparent 100%)",
          }}
        />

        {/* LINE 2 — One press of a button. */}
        <motion.div
          initial={{ opacity: 0, y: 24, filter: "blur(20px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.6, delay: 2.0, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center justify-center gap-5 sm:gap-7"
          data-testid="seven-days-line-2"
        >
          <h2
            className="kn-display text-white leading-[0.95]"
            style={{
              fontSize: "clamp(48px, 7vw, 110px)",
              fontWeight: 900,
              letterSpacing: "-0.04em",
            }}
          >
            <span style={{ color: "rgba(255,255,255,0.78)" }}>One press</span>{" "}
            <span style={{ color: "rgba(255,255,255,0.55)" }}>of a</span>{" "}
            <span
              style={{
                background:
                  "linear-gradient(135deg, #d4940a 0%, #e9a820 50%, #d4940a 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
                fontWeight: 900,
              }}
            >
              button.
            </span>
          </h2>
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{
              scale: [1, 1.15, 1],
              opacity: 1,
            }}
            transition={{
              opacity: { duration: 0.6, delay: 2.6 },
              scale: {
                duration: 1.6,
                repeat: Infinity,
                repeatDelay: 1.2,
                ease: "easeInOut",
                delay: 2.8,
              },
            }}
            className="shrink-0"
            style={{
              filter: "drop-shadow(0 0 28px rgba(233,168,32,0.7))",
            }}
          >
            <MousePointerClick
              className="w-10 h-10 sm:w-14 sm:h-14"
              style={{ color: GOLD }}
              strokeWidth={1.6}
            />
          </motion.div>
        </motion.div>
      </div>
    </SceneShell>
  );
}
